import React, { ChangeEvent, Fragment } from "react";

import { DateTime } from "luxon";

import Calendar2, { ICalendar2ButtonCallbacks } from "../Calendar2";
import DelayInput from "../DelayInput";
import ValidateInput from "../ValidateInput";

import { V2, apiGet } from "../../Api";
import { formatName } from "../../Utilities";
import withAuth from "../withAuth";
import withTitle from "../withTitle";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import OverflowMenuItem from "../OverflowMenuItem";
import Checkbox from "../Checkbox";

interface IScheduleVacationViewState {
    events: any;
    isLoading: boolean | string;

    canEditCalendar: boolean;
    month: string;

    displayAfter: DateTime;
    displayBefore: DateTime;

    searchPerson: string;
    searchType: string;

    calEmployee: string;
    calLengthType: string;
    calLength: string;
    calRepeat: boolean;
    calRepeatType: string;
    calRepeatInterval: string;
    calRepeatUntilType: string;
    calRepeatUntil: string;
    calType: string;
    calItem: any;
    calHideRepeat: boolean;

    employees: any[];

    [name: string]: any;
}

interface IScheduleVacationViewProps extends IAuthProps {
    calendarGroup: string;
}

class ScheduleVacationView extends React.Component<IScheduleVacationViewProps, IScheduleVacationViewState> {
    constructor(props: Readonly<IScheduleVacationViewProps>) {
        super(props);

        const today = DateTime.local();
        const calendarTagPermissions = ({
            [WellKnownGroups.Marketer]: "sched:mv",
            [WellKnownGroups.ClinicalOutcomesVacation]: "sched:cov",
        } as { [a: string]: string })[props.calendarGroup];

        const state: IScheduleVacationViewState = {
            events: {},
            isLoading: false,

            canEditCalendar: props.authContext.hasRight(`${calendarTagPermissions}:write`),
            month: today.startOf("month").toFormat("yyyy-MM"),

            displayBefore: today.endOf("month"),
            displayAfter: today.startOf("month"),

            searchPerson: "",
            searchType: "",

            calEmployee: "",
            calLength: "",
            calLengthType: "1",
            calRepeat: false,
            calRepeatType: "week",
            calRepeatInterval: "1",
            calRepeatUntilType: "1",
            calRepeatUntil: "1",
            calType: "0",
            calItem: {},
            calHideRepeat: false,

            employees: [],
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.displayRangeChanged = this.displayRangeChanged.bind(this);
        this.scheduleRender = this.scheduleRender.bind(this);
        this.handleDialogChange = this.handleDialogChange.bind(this);
        this.handleItemAdd = this.handleItemAdd.bind(this);
        this.handleItemEdit = this.handleItemEdit.bind(this);
        this.handleItemDelete = this.handleItemDelete.bind(this);
        this.beforeShowAddInterface = this.beforeShowAddInterface.bind(this);
        this.handleBeforeItemEdit = this.handleBeforeItemEdit.bind(this);
        this.renderDetailsButtons = this.renderDetailsButtons.bind(this);
    }

    public async componentDidMount() {
        try {
            const [employees] = await Promise.all([
                apiGet<any[]>(`employees/summary?group=${this.props.calendarGroup}&limit=100`)
            ]);

            employees.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));

            this.setState({
                employees
            });
        }
        catch (e) {
            //
        }
    }

    public render() {
        return (
            <div className="viewer-root viewer-full full-height">
                <div className="filter-control-box">
                    <DelayInput type="month" className="fill-space" timeout={500} value={this.state.month} name="month" onChangeDelayed={this.handleElementChange} />
                    <select value={this.state.searchType} name="searchType" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Leave</option>
                        <option value="0">Unspecified</option>
                        <option value="1">Vacation</option>
                        <option value="2">Sick</option>
                        <option value="3">Half-day</option>
                    </select>
                    <select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">Everyone</option>
                        {this.state.employees.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                    </select>
                </div>
                <div className="spinner-overlay-root full-height">
                    <Calendar2 className="full-height" displayMonth={this.state.month} highlightDate={new Date()} showDetailsOnClick={true} showButtonsOnDetails={true} allowAdd={this.state.canEditCalendar} allowEdit={this.state.canEditCalendar} detailsAddRender={this.scheduleRender} events={this.state.events} onDisplayRangeChanged={this.displayRangeChanged} onItemAdd={this.handleItemAdd} onBeforeShowAdd={this.beforeShowAddInterface} onBeforeEdit={this.handleBeforeItemEdit} onItemEdit={this.handleItemEdit} onItemDelete={this.handleItemDelete} detailsButtonsRender={this.renderDetailsButtons} />
                    {
                        this.state.isLoading ? (
                            <div className="spinner-overlay">
                                {
                                    (typeof this.state.isLoading === "string") ?
                                        <span className="error-text-big">{this.state.isLoading}</span> :
                                        <div className="lds-dual-ring-big" />
                                }
                            </div>
                        ) : ""
                    }
                </div>
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                events: {},
                isLoading: true,
            });

            const beginDate = this.state.displayAfter;
            const endDate = this.state.displayBefore.plus({ millisecond: 1 });

            const parameters = [
                `after=${encodeURIComponent(beginDate.toISO())}`,
                `before=${encodeURIComponent(endDate.toISO())}`,
                `group=${this.props.calendarGroup}`
            ];

            if (this.state.searchPerson) {
                parameters.push(`employee=${this.state.searchPerson}`);
            }

            if (this.state.searchType) {
                parameters.push(`type=${this.state.searchType}`);
            }

            const data = await V2.get<any[]>(`schedule/vacation?${parameters.join("&")}`);

            const monthBase = DateTime.fromISO(this.state.month);
            const events: { [date: number]: Array<{ title: string, type?: string, length?: number, item?: any }>; } = {};
            for (const item of data) {
                const dayDiff = DateTime.fromISO(item.date).diff(monthBase, "days").days + 1;
                const runLength = Math.max(Math.round(item.length || 0), 1);

                if (!events[dayDiff])
                    events[dayDiff] = [];

                const typeInfo = this.vacationTypeToInfo(item.type);

                events[dayDiff].push({
                    title: `${formatName(item.employee.name, true)} - ${typeInfo.text}`,
                    type: typeInfo.color,
                    length: runLength,
                    item
                })
            }

            this.setState({
                events,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Unable to load schedule"
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if (name === "month" && !value) {
            return;
        }

        this.setState({
            [name]: value
        }, () => {
            // this.saveSearchState();
            if (name !== "month") {
                this.loadData();
            }
        });
    }

    private displayRangeChanged(minDate: string, maxDate: string) {
        this.setState({
            displayBefore: DateTime.fromISO(maxDate).toLocal(),
            displayAfter: DateTime.fromISO(minDate).toLocal()
        }, () => this.loadData());
    }

    private handleDialogChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        });
    }

    private scheduleRender() {
        return (
            <div className="input-form">
                <div className="info-block">
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-cal-employee" className="required">Employee</label>
                            <ValidateInput type="select" id="frm-cal-employee" name="calEmployee" value={this.state.calEmployee} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={true}>
                                <option />
                                {this.state.employees.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                            </ValidateInput>
                        </div>
                        <div className="input-form-group">
                            <label htmlFor="frm-cal-type" className="required">Leave Type</label>
                            <ValidateInput type="select" id="frm-cal-type" name="calType" value={this.state.calType} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={true}>
                                <option value="0">Unspecified</option>
                                <option value="1">Vacation</option>
                                <option value="2">Sick</option>
                                <option value="3">Half-day</option>
                            </ValidateInput>
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-cal-length" className="required">Duration</label>
                            <ValidateInput type="select" id="frm-cal-length" name="calLengthType" value={this.state.calLengthType} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false}>
                                <option value="1">1 day</option>
                                <option value="2">2 days</option>
                                <option value="3">3 days</option>
                                <option value="4">4 days</option>
                                <option value="5">5 days</option>
                                <option value="0">Other</option>
                            </ValidateInput>
                        </div>
                        {
                            this.state.calLengthType === "0"
                                ? <div className="input-form-group">
                                    <label htmlFor="frm-cal-length" className="required">Days</label>
                                    <ValidateInput type="number" id="frm-cal-length" name="calLength" value={this.state.calLength} min={1} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                : <div className="input-form-group" />
                        }
                    </section>
                    {
                        !this.state.calHideRepeat
                            ? <Fragment>
                                <section>
                                    <div className="input-form-group">
                                        <div className="input-form-group-check-inline">
                                            <Checkbox fancy={true} id="frm-cal-repeat" name="calRepeat" checked={this.state.calRepeat} onChange={this.handleDialogChange} autoComplete="off_please_chrome">Repeat</Checkbox>
                                        </div>
                                    </div>
                                </section>
                                {
                                    this.state.calRepeat
                                        ? <Fragment>
                                            <section>
                                                <div className="input-form-group">
                                                    <label htmlFor="frm-cal-rep-interval" className="required">Every</label>
                                                    <div className="input-form-group-inline">
                                                        <ValidateInput type="number" id="frm-cal-rep-interval" name="calRepeatInterval" value={this.state.calRepeatInterval} onChange={this.handleDialogChange} min={1} max={520} autoComplete="off_please_chrome" isRequired={false} />
                                                        <ValidateInput type="select" id="frm-cal-rep-every" name="calRepeatType" value={this.state.calRepeatType} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false}>
                                                            <option value="week">Week(s)</option>
                                                        </ValidateInput>
                                                    </div>
                                                </div>
                                            </section>
                                            <section>
                                                <div className="input-form-group">
                                                    <label htmlFor="frm-cal-rep-until-type" className="required">Until</label>
                                                    <ValidateInput type="select" id="frm-cal-rep-until-type" name="calRepeatUntilType" value={this.state.calRepeatUntilType} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false}>
                                                        <option value="">Forever</option>
                                                        <option value="date">Date</option>
                                                    </ValidateInput>
                                                </div>
                                                {
                                                    this.state.calRepeatUntilType !== ""
                                                        ? <div className="input-form-group">
                                                            <label htmlFor="frm-cal-rep-until" className="required">Date</label>
                                                            <ValidateInput type="date" id="frm-cal-rep-until" name="calRepeatUntil" value={this.state.calRepeatUntil} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                                        </div>
                                                        : undefined
                                                }
                                            </section>
                                        </Fragment>
                                        : undefined
                                }
                            </Fragment>
                            : undefined
                    }
                </div>
            </div>
        );
    }

    private beforeShowAddInterface() {
        return new Promise<void>((resolve, reject) => {
            this.setState({
                calEmployee: this.state.searchPerson,
                calLength: "",
                calLengthType: "1",
                calRepeat: false,
                calRepeatType: "week",
                calRepeatInterval: "1",
                calRepeatUntil: "",
                calRepeatUntilType: "",
                calType: "0",
                calHideRepeat: false,
            }, () => resolve());
        });
    }

    private async handleItemAdd(date: string) {
        const daySpan = this.state.calLengthType === "0" ? this.state.calLength : this.state.calLengthType;
        if (!daySpan) {
            return;
        }

        const itemData = {
            startDate: date,
            length: +daySpan,
            employeeId: this.state.calEmployee,
            repeat: this.state.calRepeat,
            repeatType: this.state.calRepeatType,
            repeatInterval: this.state.calRepeatInterval,
            repeatUntil: this.state.calRepeatUntilType !== "" ? this.state.calRepeatUntil : "",
            type: this.state.calType || "0"
        };

        // No try..catch because the calendar dialog handles it
        await V2.post("schedule/vacation", itemData);
        this.loadData();
    }

    private async handleItemEdit(date: string, button: string) {
        const daySpan = this.state.calLengthType === "0" ? this.state.calLength : this.state.calLengthType;
        if (!daySpan || !this.state.calItem) {
            return;
        }

        let mode = "";
        switch (button) {
            case "save-all": mode = "all"; break;
            case "save":
            default: mode = "single"; break;
        }

        const itemData = {
            length: +daySpan,
            employeeId: this.state.calEmployee,
            repeat: this.state.calRepeat,
            repeatType: this.state.calRepeatType,
            repeatInterval: this.state.calRepeatInterval,
            repeatUntil: this.state.calRepeatUntilType !== "" ? this.state.calRepeatUntil : "",
            type: this.state.calType || "0"
        };

        // No try..catch because the calendar dialog handles it
        await V2.put(`schedule/vacation/${this.state.calItem.id}?date=${this.state.calItem.date}&mode=${mode}`, itemData);
        this.loadData();
    }

    private async handleItemDelete(date: string, button: string) {
        const daySpan = this.state.calLengthType === "0" ? this.state.calLength : this.state.calLengthType;
        if (!daySpan || !this.state.calItem) {
            return;
        }

        let mode;
        switch (button) {
            case "delete-selected": mode = "single"; break;
            case "delete-whole-future": mode = "all"; break;
            case "delete-whole":
            default: mode = "full"; break;
        }

        // No try..catch because the calendar dialog handles it
        await V2.delete(`schedule/vacation/${this.state.calItem.id}?date=${date}&mode=${mode}`);
        this.loadData();
    }

    private async handleBeforeItemEdit(e: any) {
        if (!e || !e.item) {
            return false;
        }

        this.setState({
            calEmployee: e.item.employee.id,
            calLengthType: !![1, 3, 5].find(a => a === e.item.length) ? e.item.length.toString() : "0",
            calLength: e.item.length.toString(),
            calItem: e.item,
            calRepeat: !!e.item.dayOfWeek || e.item.dayOfWeek === 0,
            calRepeatInterval: e.item.skipInterval + 1,
            calRepeatUntil: e.item.endDate,
            calRepeatUntilType: !!e.item.endDate ? "date" : "",
            calType: e.item.type || "0",
            calHideRepeat: false, // !e.item.dayOfWeek && e.item.dayOfWeek !== 0,
        });

        return true;
    }

    private renderDetailsButtons(callbacks: ICalendar2ButtonCallbacks) {
        if (!callbacks.isEdit) {
            return [<button key="save-button" className="button text-with-icon push-right" name="save" onClick={callbacks.save}><i className="icon-check" /><span>Save</span></button>];
        }

        return [
            <OverflowMenuItem key="delete-dropdown" icon="trash" displayText="Delete..." className="button btn-light red text-with-icon" dropdownClassName="toolbar-menu-up-right">
                <button key="delete-selected" className="button text-with-icon" name="delete-selected" onClick={callbacks.delete}><i className="icon-trash" /><span>Selected Day</span></button>
                {this.state.calItem.length > 1 ? <button key="delete-whole" className="button text-with-icon" name="delete-whole" onClick={callbacks.delete}><i className="icon-trash" /><span>All {this.state.calItem.length} day(s)</span></button> : null}
                {(!!this.state.calItem.dayOfWeek || this.state.calItem.dayOfWeek === 0) ? <button key="delete-whole-future" className="button text-with-icon" name="delete-whole-future" onClick={callbacks.delete}><i className="icon-trash" /><span>All {this.state.calItem.length} day(s) &amp; Future</span></button> : null}
            </OverflowMenuItem>,
            <OverflowMenuItem key="save-dropdown" icon="check" displayText="Save..." className="button text-with-icon push-right" dropdownClassName="toolbar-menu-up-left">
                <button key="save" className="button text-with-icon" name="save" onClick={callbacks.save}><i className="icon-check" /><span>These {this.state.calItem.length} day(s)</span></button>
                {(!!this.state.calItem.dayOfWeek || this.state.calItem.dayOfWeek === 0) ? <button key="save-all" className="button text-with-icon" name="save-all" onClick={callbacks.save}><i className="icon-calendar" /><span>All</span></button> : null}
            </OverflowMenuItem>
        ];
    }

    private vacationTypeToInfo(type: number) {
        switch (type) {
            case 0: return { color: "green", text: "Off" };
            case 1: return { color: "green", text: "Vacation" };
            case 2: return { color: "red", text: "Sick" };
            case 3: return { color: "green", text: "Half-day" };
            default: return { color: "green", text: "Off" };
        }
    }
}

function calendarType(type: string, Component: React.ComponentType<any>) {
    return (props: any) => <Component calendarGroup={type} {...props} />;
}

export default withTitle("Marketer Vacation Schedule", withAuth(calendarType(WellKnownGroups.Marketer, ScheduleVacationView)));
export const ClinicalOutcomesVacation = withTitle("CO Vacation", withAuth(calendarType(WellKnownGroups.ClinicalOutcomesVacation, ScheduleVacationView)));
